import React, { useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import withCommonEMContainer from '../_commons/CommonEMContainer/CommonEMHospitalityContainer';

//-------------------------------------
// Basic components here
//-------------------------------------
import Box from '../../elements/Box';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Text from '../../elements/Text';
import Disclaimer from '../../others/Disclaimer';
import RatesCalendar from '../../controls/FareCalendar';
import MonthNavigationDropdown from '../../controls/FareFinderCalendar/atoms/MonthNavigationDropdown';
import InfoNavigation from '../PropertyLowRatesCalendarContainer/atoms/InfoNavigation';
import DayComponentInstance from '../../items/RateCard';
import Legend from '../../others/Legend/LegendHotelLowRateCalendar';
import Loading from '../../others/Loading';
import NoFaresBehavior from '../../others/NoFaresBehavior/NoFaresBehavior';
import Skeleton from '../../others/Skeleton/SkeletonHotelLowRateCalendar';
import PropertyAlert from './atoms/PropertyAlert';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';

//-------------------------------------
// Every mundo integration
//-------------------------------------
import { getHistograms } from '../../../_integrations/everymundo/rates';
import { getProperties } from '../../../_integrations/everymundo/locationsEM';

//-------------------------------------
// Commons functions
//-------------------------------------
import { useFilters } from '../_commons/hospitality/filters';
import { useFareItemProps } from '../_commons/tracking';
import InlineFilters from '../../controls/HospitalityInlineFilters/PropertyInlineFilter';
import SecondaryFilters from '../../controls/HospitalitySecondaryFilters/PropertySecondaryFilters';
import { getResultsLength } from '../_commons/calendar';
import useStayLengthDisabledSettings from './filters/useStayLengthDisabled';
import useAccessible from './filters/useAccessible';

import prePopulationDefaultProps from '../_commons/prepopulationDefaultProps/histogram';
import HeaderContainer from '../../elements/HeaderContainer';
import useFiltersDisableSettings from '../_commons/hospitality/useFiltersDisabled';

const getRatesTags = () => {};

function PropertyLowRatesCalendarContainer(props) {
  const {
    prePopulation,
    integrationSettings,
    meta,
    ratesCalendar,
    legend,
    loading,
    direction,
    translate,
    initialHistograms,
    getProperties: getPropertiesFn,
    getHistograms: getHistogramsFn,
    fareItemOnClickAction,
    showDisclaimer,
    showFilters,
    showSecondaryFilters,
    secondaryFilters,
    showLegend,
    skeletonSettings,
    disclaimerContentSettings,
    mainContentSettings,
    legendContentSettings,
    filterContentSettings,
    noResultsPlaceHolder,
    alert,
    minInitialResults,
    theme,
    filters,
    daysLeftForAutomatedRollover,
    disclaimer,
    alertInfo,
    alertInfoTextSettings,
    enableSEO = true,
    isIframe
  } = props;

  // Disabling the scroll on the product page to the calculation that VG makes for the height of the Iframe
  if (isIframe && !isSSR()) {
    document.body.style.overflow = 'hidden';
  }

  const [showThresholdDay, setThresholdDay] = useState(!!daysLeftForAutomatedRollover);
  const [className, style] = useStyles(props, { direction, theme });
  const myStyle = Object.assign({ minHeight: '600px' }, style);

  // Define filter props using deprecated container props (for backwards compatibility)
  // Don't rely on this props for new configurations, they will be removed in a future version
  const filtersConfig = {
    containerSettings: filterContentSettings,
    ...filters
  };
  const { months = 3, defaultMonth = 1 } = ratesCalendar;

  const initialMonth = dayjs().add(prePopulation.dateRange.start, 'day').toDate();

  const daysLeftForAutomatedRolloverEnable =
    daysLeftForAutomatedRollover &&
    showThresholdDay &&
    dayjs(initialMonth).date() > dayjs(initialMonth).daysInMonth() - daysLeftForAutomatedRollover;

  const rateTags = secondaryFilters?.rateTagsSettings?.values;

  const {
    state,
    fares: { match, ...fares },
    dispatch,
    tracking,
    retry,
    ...filtersState
  } = useFilters({
    filter: {
      prePopulation,
      integrationSettings,
      meta,
      getProperties: getPropertiesFn,
      maxItemsAmount: 1,
      nextMonth: daysLeftForAutomatedRolloverEnable,
      defaultMonth: defaultMonth <= months ? defaultMonth : 1
    },
    initialData: initialHistograms,
    rateTagsData: rateTags,
    stayLengthData: secondaryFilters?.stayLengthSettings?.options,
    getFares: getHistogramsFn,
    getRatesTags,
    faresTransformationLogic: 'histogram',
    minInitialResults,
    showNoResultsPlaceholder: false,
    availableFilters: showFilters ? filtersConfig.availableFilters : [],
    availableSecondaryFilter: secondaryFilters ? secondaryFilters.availableFilters : [],
    enabledHistogram: true,
    getResultsLength
  });

  const { dateRange, stayLength, ratesTags, property } = filtersState;

  React.useEffect(() => {
    if (fares.data) {
      const isEmpty = fares.data.every((fare) => fare.fareItem.dataType === 'EMPTY_FROM_API');
      isEmpty && tracking({ type: 'NO_FARES_AVAILABLE' });
    }
  }, [tracking, fares.data]);

  React.useEffect(() => {
    if (
      ratesTags.selectedItems.length === 1 &&
      ratesTags.selectedItems.find((item) => item.value === 'WEEKEND')
    ) {
      const dayNextFocus = fares.data?.find((item) => item.fareItem.dataType !== 'EMPTY');
      if (dayNextFocus && state.firstDay !== dayNextFocus.day) {
        dispatch({ type: 'SET_FOCUS_DATE_WEEKEND', payload: dayNextFocus.day });
      }
    }
  }, [dispatch, ratesTags.selectedItems, state.firstDay, fares.data]);

  const dayCard = useFareItemProps(ratesCalendar.dayCard, dispatch, fareItemOnClickAction);
  const dayCardAccessible = useAccessible(dayCard, fares.data, state, dispatch);

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  if (match('HIDDEN')) {
    return <NoFaresBehavior />;
  }

  const changeMonth = (month) => {
    dispatch({ type: 'MONTH_CHANGE', payload: month });
    setThresholdDay(false);
  };

  function handleSeeMoreDeals() {
    tracking({ type: 'MORE_DEALS' });
    return true;
  }
  const { options = [] } = secondaryFilters.stayLengthSettings;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const stayLengthInfo = React.useMemo(() => {
    const item =
      typeof stayLength.value === 'string' ? stayLength.value : JSON.stringify(stayLength.value);
    if (Boolean(stayLength)) {
      const value = options.find((i) => JSON.stringify(i.value) === item);
      return value
        ? translate(value.secondaryLabel, 'any stay length')
        : translate('cmp-property-low-rates-any', 'any stay length');
    } else {
      return translate('cmp-property-low-rates-any', 'any stay length');
    }
  }, [stayLength, options, translate]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { stayLengthDisabledSettings, stayLengthInfoLabel } = useStayLengthDisabledSettings(
    prePopulation.disabledStayLength,
    { ...secondaryFilters.stayLengthSettings },
    {
      ratesTags: ratesTags.selectedItems
    }
  );

  const isPropertySelected = Boolean(property.property.value);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const secondaryFiltersDisableSettings = useFiltersDisableSettings(
    { ...filters, ...secondaryFilters },
    {
      ratesTags: !isPropertySelected,
      stayLength: !isPropertySelected
    }
  );

  const renderAlertInfo = !isPropertySelected && (
    <Box dataTestId="no-rates-message" {...alertInfo}>
      <Text {...alertInfoTextSettings}>
        {translate(
          'cmp-property-low-rates-calendar-alert-info',
          'Please input the property in order to display average daily rates.'
        )}
      </Text>
    </Box>
  );

  return (
    <Box
      position="relative"
      className={`${className} ${PropertyLowRatesCalendarContainer.componentName}`}
      style={myStyle}
      dataTestId="lowFareCalendarContainer"
    >
      {match('SKELETON') && <Skeleton {...skeletonSettings} position="absolute" width="100" />}
      {match('LOADING') && <Loading {...loading} />}
      <HeaderContainer
        defaultTitleValue={'Find the best rate for your next getaway'}
        defaultSubtitleValue={'Discover the best deals with our low rate calendar.'}
        handleSeeMoreDeals={handleSeeMoreDeals}
        {...props}
      />
      {showFilters && <InlineFilters {...filtersConfig} {...filtersState} />}
      {showSecondaryFilters && (
        <SecondaryFilters
          {...filtersConfig}
          {...secondaryFilters}
          {...stayLengthDisabledSettings}
          {...secondaryFiltersDisableSettings}
          {...filtersState}
          availableFilters={filtersState.availableSecondaryFilter}
          filter={{ ...{ prePopulation, integrationSettings, meta } }}
          loading={match('SKELETON')}
        />
      )}
      {renderAlertInfo}

      <Row dataTestId="rates-container" {...mainContentSettings} style={{ position: 'relative' }}>
        {match('ERROR') && (
          <Col>
            <ContainerErrorAlert {...alert} tryAgain={retry} />
          </Col>
        )}
        {match('WAITING_FOR_PROPERTY') && (
          <Col>
            <PropertyAlert {...noResultsPlaceHolder} />
          </Col>
        )}
        {match('COMPLETE') && (
          <>
            <Col md="12">
              <RatesCalendar
                {...ratesCalendar}
                dayCard={dayCardAccessible}
                initialMonth={initialMonth}
                currentMonth={dateRange.value.start}
                data={fares.data}
                NavComponentInstance={MonthNavigationDropdown}
                renderNavInfo={
                  <InfoNavigation
                    stayLength={stayLengthInfoLabel !== '' ? stayLengthInfoLabel : stayLengthInfo}
                    {...ratesCalendar.navInfoSettings}
                  />
                }
                onSelectMonth={changeMonth}
                loading={match('LOADING')}
                DayComponentInstance={DayComponentInstance}
              />
            </Col>
            {showLegend && (
              <Col md="12" {...legendContentSettings}>
                <Legend {...legend} />
              </Col>
            )}
          </>
        )}
      </Row>
      {showDisclaimer && (
        <Row {...disclaimerContentSettings}>
          <Col>
            <Disclaimer
              floatLeft
              labelKey="cmp-property-low-rates-calendar-container-disclaimer"
              {...disclaimer}
            />
          </Col>
        </Row>
      )}
    </Box>
  );
}

PropertyLowRatesCalendarContainer.propTypes = {
  /**
   * Props of alerts
   */
  alert: PropTypes.objectOf(PropTypes.any),
  /**
   * Direction of the component rtl o ltr
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * Disclaimer Component
   */
  disclaimer: PropTypes.objectOf(PropTypes.any),
  /**
   * disclaimerContentSettings props
   */
  disclaimerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * RatesCalendar Component props
   */
  ratesCalendar: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  fareItemInteractionType: PropTypes.oneOf(['popup', 'deeplink']),
  /**
   * Handle the action when click on card
   */
  fareItemOnClickAction: PropTypes.func,
  /**
   * filterContentSettings props
   */
  filterContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters settings
   */
  filters: PropTypes.objectOf(PropTypes.any),
  /**
   * Function for request to obtain propertys
   */
  getProperties: PropTypes.func,
  getRatesTags: PropTypes.func,
  /**
   * Function for request to obtain histograms
   */
  getHistograms: PropTypes.func,
  /**
   * Hide the component if the ammount of initial results is lower than this value
   */
  minInitialResults: PropTypes.number,
  /**
   * Used to prepopulate the component
   */
  initialHistograms: PropTypes.arrayOf(PropTypes.any),
  /**
   * All integration configurations
   */
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * legend Component props
   */
  legend: PropTypes.objectOf(PropTypes.any),
  /**
   * legend content settings
   */
  legendContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Loading Component
   */
  loading: PropTypes.objectOf(PropTypes.any),
  /**
   * mainContentSettings props
   */
  mainContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of noResultsPlaceHolder
   */
  noResultsPlaceHolder: PropTypes.objectOf(PropTypes.any),
  /**
   * Page Context
   */
  pageContext: PropTypes.shape({
    deviceCategory: PropTypes.string,
    siteEdition: PropTypes.string
  }),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Determine if the component will show disclaimer
   */
  showDisclaimer: PropTypes.bool,
  /**
   * showFilters
   */
  showFilters: PropTypes.bool,
  /**
   * showLegend
   */
  showLegend: PropTypes.bool,
  /**
   * Determine if the component will show the title
   */
  showTitle: PropTypes.bool,
  /**
   * SwitchOriginDestination Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Title Component
   */
  title: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  titleKey: PropTypes.string,
  /**
   * Function to translate the component
   */
  translate: PropTypes.func,
  /**
   * travelClass options
   */
  tracking: PropTypes.objectOf(PropTypes.any),
  /**
   * LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * Metadata configuration
   */
  meta: PropTypes.objectOf(PropTypes.any),

  /**
   * Show the SubTitle Component
   */
  showSubTitle: PropTypes.bool,
  /**
   * subHeaderContentSettings props
   */
  subHeaderContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  subTitleKey: PropTypes.string,
  /**
   * SubTitle Component
   */
  subTitleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * daysLeftForAutomatedRollover indicate how many days before the end of the month it should show the calendar of low rates for the next month
   */
  daysLeftForAutomatedRollover: PropTypes.number,
  /**
   * navInfoSettings props
   */
  navInfoSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * isIframe
   */
  isIframe: PropTypes.bool
};
PropertyLowRatesCalendarContainer.defaultProps = {
  alert: {
    bgColor: 'secondary-4',
    textAlign: 'center',
    padding: '3',
    rounded: '@theme:card.rounded'
  },
  direction: 'ltr',
  disclaimer: {
    fontSize: '3'
  },
  disclaimerContentSettings: {
    marginTop: '2'
  },
  ratesCalendar: {},
  fareItemInteractionType: 'popup',
  fareItemOnClickAction: null,
  filterContentSettings: {
    marginTop: '4'
  },
  filters: {},
  getRatesTags: () => {},
  getProperties,
  getHistograms,
  minInitialResults: null,
  initialHistograms: null,
  integrationSettings: {},
  legend: {},
  legendContentSettings: {
    marginTop: '2'
  },
  loading: {},
  mainContentSettings: {
    marginTop: '4'
  },
  noResultsPlaceHolder: {
    bgColor: 'light'
  },
  pageContext: {
    deviceCategory: 'DESKTOP',
    siteEdition: 'en_HK'
  },
  prePopulation: prePopulationDefaultProps,
  showDisclaimer: true,
  showFilters: true,
  showLegend: true,
  showTitle: true,
  skeletonSettings: {
    style: { backgroundColor: 'white', paddingBottom: '1.5rem' }
  },
  title: {},
  titleKey: 'cmp-property-low-rates-calendar-container-title',
  translate: () => {},
  tracking: {
    passengerCount: 2,
    eventType: 'em-cmp-property-low-rates-calendar'
  },
  localizationData: null,
  theme: null,
  meta: null,
  showSubTitle: false,
  subHeaderContentSettings: {},
  subTitleKey: 'cmp-property-low-rates-calendar-sub-title',
  subTitleSettings: {},
  daysLeftForAutomatedRollover: 5,
  navInfoSettings: {},
  isIframe: false
};
PropertyLowRatesCalendarContainer.componentName = 'PropertyLowRatesCalendarContainer';

// HOC with common functionality to all containers
export default withCommonEMContainer(PropertyLowRatesCalendarContainer);
